import { ButtonLoading } from "@/components/button-loading";
import { formatNumber, formatPrice } from "@/lib/format-amount";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { ApiCreditPackageDTO } from "@shaarp/common";
import { ArrowRightIcon, CoinsIcon } from "lucide-react";
import { useEffect, useState } from "react";

interface DialogPurchaseApiCreditsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DialogPurchaseApiCredits({
  open,
  onOpenChange,
}: DialogPurchaseApiCreditsProps) {
  const { t } = useTranslation();
  const api = useApi();
  const toastError = useToastError();

  const [packages, setPackages] = useState<ApiCreditPackageDTO[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (open) {
      fetchPackages();
    }
  }, [open]);

  const fetchPackages = async () => {
    try {
      setIsFetching(true);
      const { data } = await api.get<ApiCreditPackageDTO[]>(
        "/my/team/api-credits/packages"
      );
      setPackages(data);
      const popularIndex = data.findIndex((p) => p.popular);
      setSelectedIndex(popularIndex >= 0 ? popularIndex : 0);
    } catch {
      toastError();
    } finally {
      setIsFetching(false);
    }
  };

  const selectedPkg = packages[selectedIndex];
  const promo = selectedPkg?.betaPromo;
  const displayPrice = promo?.active
    ? promo.discountedPriceOneShot
    : selectedPkg?.priceOneShot;

  const handlePurchase = async () => {
    if (!selectedPkg) return;
    setIsLoading(true);

    try {
      const { data } = await api.post<{ checkoutUrl: string }>(
        "/my/team/api-credits/purchase-oneshot",
        { packageId: selectedPkg.id }
      );

      if (data.checkoutUrl) {
        sessionStorage.setItem("stripe-api-checkout-package", selectedPkg.id);
        window.location.href = data.checkoutUrl;
      }
    } catch {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const formatCredits = formatNumber;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-2">
              <div className="flex justify-center items-center w-8 h-8 bg-gray-100 rounded-full">
                <CoinsIcon className="w-4 h-4 text-gray-600" />
              </div>
              <span className="font-semibold text-gray-900">
                {t("team.apiCredits.purchase.title")}
              </span>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="oneshot">
          <TabsList className="w-full">
            <TabsTrigger value="oneshot" className="flex-1">
              {t("team.apiCredits.purchase.tabOneshot")}
            </TabsTrigger>
            <TabsTrigger value="subscription" disabled className="flex-1 gap-2">
              {t("team.apiCredits.purchase.tabSubscription")}
              <Badge variant="secondary" className="text-xs">
                {t("team.apiCredits.purchase.comingSoon")}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="oneshot">
            {isFetching || packages.length === 0 ? (
              <div className="flex justify-center items-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
              </div>
            ) : (
              <>
                <div className="mb-6 text-center">
                  <div className="flex gap-3 justify-center items-center mb-4">
                    <h2 className="text-3xl font-bold text-gray-900">
                      {formatCredits(selectedPkg.credits)}{" "}
                      {t("team.apiCredits.purchase.credits")}
                    </h2>
                    {promo?.active && (
                      <Badge className="bg-red-500 text-white hover:bg-red-600 text-sm">
                        {t("team.apiCredits.purchase.promoLabel", {
                          percent: promo.percentOff,
                        })}
                      </Badge>
                    )}
                  </div>

                  <div className="mb-6">
                    <div className="flex justify-between mb-2 px-2">
                      {packages.map((pkg, index) => (
                        <div key={pkg.id} className="flex flex-col items-center">
                          <span
                            className={`text-sm font-medium transition-colors ${
                              selectedIndex === index
                                ? "font-bold"
                                : "text-gray-400"
                            }`}
                          >
                            {formatCredits(pkg.credits)}
                          </span>
                          <div className="w-px h-2 bg-gray-300" />
                        </div>
                      ))}
                    </div>
                    <Slider
                      value={[selectedIndex]}
                      onValueChange={(value) => setSelectedIndex(value[0])}
                      max={packages.length - 1}
                      min={0}
                      step={1}
                      className="w-full transition-colors duration-100 cursor-pointer hover:bg-gray-300"
                    />
                  </div>

                  <p className="text-sm text-gray-600">
                    {formatPrice(displayPrice / selectedPkg.credits)}{" "}
                    {t("team.apiCredits.purchase.perCredit")}
                  </p>
                </div>

                <div className="flex flex-col pt-4 mb-4 space-y-4 border-t">
                  <div className="flex gap-4 justify-end items-center">
                    <div className="text-right">
                      {promo?.active ? (
                        <>
                          <div className="text-sm text-gray-400 line-through">
                            {formatPrice(selectedPkg.priceOneShot)}
                          </div>
                          <div className="text-2xl font-bold text-red-600">
                            {formatPrice(promo.discountedPriceOneShot)}
                          </div>
                        </>
                      ) : (
                        <div className="text-2xl font-bold">
                          {formatPrice(selectedPkg.priceOneShot)}
                        </div>
                      )}
                      <div className="text-sm text-gray-500">
                        {t("team.credits.purchase.oneTimePayment")}
                      </div>
                    </div>
                    <ButtonLoading
                      onClick={handlePurchase}
                      disabled={isLoading}
                      className="py-3 font-medium rounded-lg grow-0"
                      isLoading={isLoading}
                    >
                      <ArrowRightIcon className="mr-2 w-4 h-4" />
                      {t("team.apiCredits.purchase.buyNow")}
                    </ButtonLoading>
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
